'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

interface NavItem {
  label: string;
  href: string;
  icon: string;
  badge?: number;
  badgeVariant?: 'danger' | 'warning' | 'info';
  children?: { label: string; href: string }[];
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard', href: '/network-monitoring-dashboard', icon: 'LayoutDashboardIcon' },
  { label: 'Hosts', href: '/host-management', icon: 'ServerIcon' },
  { label: 'POP', href: '/pop-management', icon: 'MapPinIcon' },
  { label: 'Node', href: '/node-management', icon: 'GitBranchIcon' },
  { label: 'Alerts', href: '/alert-notification-log', icon: 'BellIcon', badge: 3, badgeVariant: 'danger' },
  { label: 'Notifications', href: '/notification-settings', icon: 'MailIcon' },
  { label: 'Report', href: '/network-measurement', icon: 'ActivityIcon' },
  { label: 'Report POP', href: '/pop-report', icon: 'BarChart2Icon' },
  { label: 'Report Node', href: '/node-report', icon: 'BarChart3Icon' },
  {
    label: 'Traffic',
    href: '/traffic/dashboard',
    icon: 'GaugeIcon',
    children: [
      { label: 'Dashboard', href: '/traffic/dashboard' },
      { label: 'Router', href: '/traffic/routers' },
    ],
  },
];

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export default function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const pathname = usePathname();
  const router = useRouter();
  const [adminName, setAdminName] = useState('');
  const [adminInitials, setAdminInitials] = useState('AD');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(['/traffic/dashboard']));

  useEffect(() => {
    const loadSession = () => {
      fetch('/api/auth/session')
        .then(r => r.json())
        .then(({ user }) => {
          if (user?.full_name) {
            setAdminName(user.full_name);
            const parts = user.full_name.split(' ');
            setAdminInitials(parts.map((w: string) => w[0]).slice(0, 2).join('').toUpperCase());
          }
        })
        .catch(() => {});
    };

    loadSession();

    const handleProfileUpdate = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.full_name) {
        setAdminName(detail.full_name);
        const parts = detail.full_name.split(' ');
        setAdminInitials(parts.map((w: string) => w[0]).slice(0, 2).join('').toUpperCase());
      }
    };
    window.addEventListener('profile-updated', handleProfileUpdate);
    return () => window.removeEventListener('profile-updated', handleProfileUpdate);
  }, []);

  const handleLogout = async () => {
    await fetch('/api/auth/logout', { method: 'POST' });
    router.replace('/login');
  };

  const badgeColor = (variant?: string) => {
    if (variant === 'danger') return 'bg-red-500 text-white';
    if (variant === 'warning') return 'bg-amber-500 text-white';
    return 'bg-blue-500 text-white';
  };

  const toggleGroup = (href: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(href)) next.delete(href);
      else next.add(href);
      return next;
    });
  };

  const isGroupActive = (item: NavItem) => {
    if (!item.children) return false;
    return item.children.some(c => pathname === c.href || pathname.startsWith(c.href + '/'));
  };

  return (
    <aside
      className={`
        fixed left-0 top-0 h-full z-30 flex flex-col
        bg-white border-r border-border
        transition-all duration-300 ease-in-out
        ${collapsed ? 'w-16' : 'w-60'}
      `}
    >
      {/* Logo */}
      <div className={`flex items-center h-16 px-3 border-b border-border ${collapsed ? 'justify-center' : 'justify-between'}`}>
        <div className="flex items-center gap-2 overflow-hidden">
          <AppLogo size={32} />
          {!collapsed && (
            <span className="font-semibold text-[15px] text-foreground whitespace-nowrap">
              NetWatch
            </span>
          )}
        </div>
        {!collapsed && (
          <button
            onClick={onToggle}
            className="p-1.5 rounded-md hover:bg-muted transition-colors text-muted-foreground"
            aria-label="Collapse sidebar"
          >
            <Icon name="PanelLeftCloseIcon" size={16} />
          </button>
        )}
      </div>

      {/* Nav Items */}
      <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
        {NAV_ITEMS.map((item) => {
          if (item.children) {
            const groupActive = isGroupActive(item);
            const expanded = expandedGroups.has(item.href) || groupActive;
            return (
              <div key={`nav-group-${item.href}`}>
                <button
                  onClick={() => !collapsed && toggleGroup(item.href)}
                  className={`
                    w-full group flex items-center gap-3 px-2.5 py-2.5 rounded-lg
                    text-[13.5px] font-medium transition-all duration-150
                    ${groupActive
                      ? 'bg-primary-50 text-primary-500 shadow-sm'
                      : 'text-foreground-secondary hover:bg-muted hover:text-foreground'
                    }
                    ${collapsed ? 'justify-center' : ''}
                  `}
                  title={collapsed ? item.label : undefined}
                >
                  <span className={`flex-shrink-0 ${groupActive ? 'text-primary-500' : 'text-muted-foreground group-hover:text-foreground'}`}>
                    <Icon name={item.icon as any} size={18} />
                  </span>
                  {!collapsed && (
                    <>
                      <span className="flex-1 whitespace-nowrap text-left">{item.label}</span>
                      <Icon
                        name="ChevronDownIcon"
                        size={13}
                        className={`text-muted-foreground transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
                      />
                    </>
                  )}
                </button>
                {!collapsed && expanded && (
                  <div className="ml-4 mt-0.5 space-y-0.5 border-l border-border pl-3">
                    {item.children.map(child => {
                      const childActive = pathname === child.href || pathname.startsWith(child.href + '/');
                      return (
                        <Link
                          key={`nav-child-${child.href}`}
                          href={child.href}
                          className={`
                            flex items-center gap-2 px-2 py-2 rounded-lg
                            text-[13px] font-medium transition-all duration-150
                            ${childActive
                              ? 'text-primary-500 bg-primary-50' :'text-muted-foreground hover:bg-muted hover:text-foreground'
                            }
                          `}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${childActive ? 'bg-primary-500' : 'bg-muted-foreground/40'}`} />
                          {child.label}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }

          const isActive = pathname === item.href || pathname.startsWith(item.href + '/');
          return (
            <Link
              key={`nav-${item.href}`}
              href={item.href}
              className={`
                group flex items-center gap-3 px-2.5 py-2.5 rounded-lg
                text-[13.5px] font-medium transition-all duration-150
                ${isActive
                  ? 'bg-primary-50 text-primary-500 shadow-sm'
                  : 'text-foreground-secondary hover:bg-muted hover:text-foreground'
                }
                ${collapsed ? 'justify-center' : ''}
              `}
              title={collapsed ? item.label : undefined}
            >
              <span className={`flex-shrink-0 ${isActive ? 'text-primary-500' : 'text-muted-foreground group-hover:text-foreground'}`}>
                <Icon name={item.icon as any} size={18} />
              </span>
              {!collapsed && (
                <span className="flex-1 whitespace-nowrap">{item.label}</span>
              )}
              {!collapsed && item.badge !== undefined && (
                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${badgeColor(item.badgeVariant)}`}>
                  {item.badge}
                </span>
              )}
              {collapsed && item.badge !== undefined && (
                <span className={`absolute left-8 top-1 w-2 h-2 rounded-full ${item.badgeVariant === 'danger' ? 'bg-red-500' : 'bg-amber-500'}`} />
              )}
            </Link>
          );
        })}
      </nav>

      {/* Bottom */}
      <div className={`border-t border-border p-2 space-y-1 ${collapsed ? 'items-center flex flex-col' : ''}`}>
        {collapsed && (
          <button
            onClick={onToggle}
            className="p-2 rounded-md hover:bg-muted transition-colors text-muted-foreground w-full flex justify-center"
            aria-label="Expand sidebar"
          >
            <Icon name="PanelLeftOpenIcon" size={16} />
          </button>
        )}
        <Link
          href="/profile"
          className={`flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-muted transition-colors cursor-pointer ${collapsed ? 'justify-center' : ''}`}
          title={collapsed ? 'Profil' : undefined}
        >
          <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
            <span className="text-[11px] font-semibold text-primary-500">{adminInitials}</span>
          </div>
          {!collapsed && (
            <div className="overflow-hidden flex-1">
              <p className="text-[13px] font-medium text-foreground truncate">{adminName || 'Admin'}</p>
              <p className="text-[11px] text-muted-foreground truncate">IT Team</p>
            </div>
          )}
          {!collapsed && (
            <Icon name="SettingsIcon" size={13} className="text-muted-foreground flex-shrink-0" />
          )}
        </Link>
        <button
          onClick={handleLogout}
          className={`flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-red-50 hover:text-red-600 transition-colors text-muted-foreground w-full ${collapsed ? 'justify-center' : ''}`}
          title={collapsed ? 'Keluar' : undefined}
        >
          <Icon name="LogOutIcon" size={16} />
          {!collapsed && <span className="text-[13px] font-medium">Keluar</span>}
        </button>
      </div>
    </aside>
  );
}